import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import moment from 'moment';
import ContentLoader from '../loader/ContentLoader';
import { SOMETHING_WENT_WRONG } from '../../constants/strings';
import { agentsService } from '../../services/agents.service';
import { currencySymbol } from '../../helper/commonHelper';
import {
  Badge, DETAIL_ACCENTS, EmptyState as Empty, HeroChip, Pill, ProfileHero, detailTableWrap, detailThStyle as thStyle, isEmpty, show, titleCase,
} from '../common/DetailUI';
import { ACTION_ICONS } from '../../config/siteConfig';
import { MarkupsSection, fileUrl } from '../common/AccountSections';
import { ProfileFields as Fields, ProfileSection as Section, profileTdStyle as tdStyle } from '../common/ProfileBlocks';

const MySwal = withReactContent(Swal);

/* ---------- styles ---------- */
const btnLight = { background: "#fff", border: "1px solid #e8edf5", borderRadius: 8, height: 34, fontSize: 13, color: "#444" };
const btnPrimary = { background: "var(--btn-bg)", borderRadius: 8, height: 34, fontSize: 13, fontWeight: 600 };
const linkStyle = { color: "var(--btn-bg)", textDecoration: "none" };

// one accent per section so the page reads as colour-coded blocks
const ACCENTS = {
  company: DETAIL_ACCENTS.indigo,
  contact: DETAIL_ACCENTS.sky,
  address: DETAIL_ACCENTS.teal,
  record: "#64748b",
};

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "-");
const formatDay = (d) => (d ? moment(d).format("DD MMM YYYY") : "-");
const maskAccount = (n) => (isEmpty(n) ? "-" : String(n).length <= 4 ? n : "•••• " + String(n).slice(-4));
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/* ---------- Documents (read-only list) ---------- */
const DocumentsList = ({ documents, accent = DETAIL_ACCENTS.violet }) => {
  const verified = documents.filter((d) => String(d.verification_status).toLowerCase() === "verified").length;
  return (
    <Section icon="bi-file-earmark-text-fill" title="Documents" subtitle={`${plural(documents.length, "document")} · ${verified} verified`} accent={accent}>
      {documents.length > 0 ? (
        <div className="table-responsive" style={detailTableWrap}>
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>{["Type", "Name", "Number", "Uploaded", "Status", "Verified On", "Remarks", "Action"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {documents.map((d) => {
                const url = fileUrl(d.doc_url);
                return (
                  <tr key={d.id}>
                    <td style={tdStyle}>{titleCase(d.doc_type)}</td>
                    <td style={tdStyle}>{show(d.doc_name)}</td>
                    <td style={tdStyle}>{show(d.doc_number)}</td>
                    <td style={tdStyle}>{formatDay(d.created_at)}</td>
                    <td style={tdStyle}><Badge value={d.verification_status} /></td>
                    <td style={tdStyle}>{d.verified_at ? formatDay(d.verified_at) : "-"}</td>
                    <td style={{ ...tdStyle, whiteSpace: "normal", minWidth: 140 }}>{show(d.remarks)}</td>
                    <td style={tdStyle}>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer" title="View" className="btn btn-sm d-inline-flex align-items-center justify-content-center"
                          style={{ width: 32, height: 32, borderRadius: 8, background: "#e6f7f1", color: "#0f8a5f", border: "none", padding: 0 }}>
                          <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                        </a>
                      ) : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <Empty text="No documents uploaded" />}
    </Section>
  );
};

/* ---------- Bank details (read-only list) ---------- */
const BankList = ({ banks, accent = DETAIL_ACCENTS.pink }) => {
  const [revealed, setRevealed] = useState({});
  return (
    <Section icon="bi-bank2" title="Bank Details" subtitle={plural(banks.length, "account")} accent={accent}>
      {banks.length > 0 ? (
        <div className="table-responsive" style={detailTableWrap}>
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>{["Bank Name", "Account Holder", "Account Number", "Branch", "Type", "IFSC Code", "Primary", "Status", "Added On"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {banks.map((b) => {
                const shown = !!revealed[b.id];
                return (
                  <tr key={b.id}>
                    <td style={tdStyle}>{show(b.bank_name)}</td>
                    <td style={tdStyle}>{show(b.account_holder_name)}</td>
                    <td style={tdStyle}>
                      <span className="d-inline-flex align-items-center gap-2">
                        <span style={{ letterSpacing: 0.5 }}>{shown ? b.account_number : maskAccount(b.account_number)}</span>
                        {!isEmpty(b.account_number) && (
                          <button type="button" title={shown ? "Hide" : "Show"} className="btn btn-sm p-0 border-0" style={{ color: accent, lineHeight: 1 }}
                            onClick={() => setRevealed((prev) => ({ ...prev, [b.id]: !prev[b.id] }))}>
                            <i className={"bi " + (shown ? "bi-eye-slash" : "bi-eye")}></i>
                          </button>
                        )}
                      </span>
                    </td>
                    <td style={tdStyle}>{show(b.branch_name)}</td>
                    <td style={tdStyle}>{titleCase(b.account_type)}</td>
                    <td style={tdStyle}>{show(b.ifsc_code)}</td>
                    <td style={tdStyle}>{b.is_primary ? <Pill value="Primary" accent={accent} /> : "-"}</td>
                    <td style={tdStyle}><Badge value={b.status} /></td>
                    <td style={tdStyle}>{formatDay(b.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <Empty text="No bank accounts added" />}
    </Section>
  );
};
const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : "");
const humanOr = (v, fallback) => (isEmpty(v) ? fallback : titleCase(v));

const AgentDetails = () => {
  const { agentCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [agent, setAgent] = useState(null);

  useEffect(() => {
    loadDetail();
  }, [agentCode]);

  /* ---------- API: details by agent code ---------- */
  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await agentsService.detailsByCode(agentCode);
      if (res?.success) {
        setAgent(res.resData);
      } else {
        setAgent(null);
        toast.error(res?.message);
      }
    } catch (err) {
      MySwal.fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fullName = agent ? [agent.title, agent.first_name, agent.last_name].filter(Boolean).join(" ") : "";
  const location = agent ? [agent.city?.name, agent.state?.name, agent.country?.name].filter(Boolean).join(", ") : "";
  const wallet = agent?.wallet;
  const symbol = currencySymbol(wallet?.currency_code);
  const money = (v) => (isEmpty(v) ? "-" : `${symbol} ${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
  const documents = agent?.documents || [];
  const markups = agent?.markups || [];

  return (
    <section className="section dashboard">
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>Agent Details</h4>
          <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
            <Link to="/dashboard" style={{ color: "#475569" }}><i className="bi bi-house-door"></i></Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/setting" style={{ color: "#475569" }}>Setting</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <Link to="/agency/list" style={{ color: "#475569" }}>Agency List</Link>
            <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
            <span className="fw-semibold" style={{ color: "#4a5568" }}>{agentCode}</span>
          </nav>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button type="button" onClick={loadDetail} className="btn btn-sm d-flex align-items-center gap-1 px-3" style={btnLight}>
            <i className="bi bi-arrow-clockwise"></i> Refresh
          </button>
          {agent && (
            <button type="button" onClick={() => navigate("/agency/add", { state: { agentId: agent.id } })}
              className="btn btn-sm d-flex align-items-center gap-1 px-3" style={btnLight}>
              <i className="bi bi-pencil"></i> Edit
            </button>
          )}
          <Link to="/agency/list" className="btn btn-sm d-flex align-items-center gap-1 text-white px-3" style={btnPrimary}>
            <i className="bi bi-list-ul"></i> Agency List
          </Link>
        </div>
      </div>

      {loading ? <ContentLoader loaderType="circleSpin" /> : !agent ? (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #eef1f7", padding: 40, textAlign: "center" }}>
          <i className="bi bi-search" style={{ fontSize: 32, color: "#b4bdd0" }}></i>
          <div className="fw-semibold mt-2" style={{ color: "#4a5568" }}>Agent not found</div>
          <div style={{ fontSize: 13, color: "#475569" }}>No agent exists with code {agentCode}.</div>
        </div>
      ) : (
        <>
          <ProfileHero
            logo={fileUrl(agent.logo_url)}
            icon="bi-building"
            title={agent.company_name}
            // right={<Badge value={agent.status} />}
            // chips={
            //   <>
            //     <HeroChip icon="bi-hash">{agent.agent_code}</HeroChip>
            //     {agent.registered_as && <HeroChip icon="bi-briefcase">{titleCase(agent.registered_as)}</HeroChip>}
            //   </>
            // }
            meta={[
              { icon: "bi-person", text: fullName || agent.executive },
              { icon: "bi-telephone", text: agent.mobile, href: agent.mobile ? `tel:${agent.mobile}` : null },
              { icon: "bi-envelope", text: agent.email, href: agent.email ? `mailto:${agent.email}` : null },
              //{ icon: "bi-geo-alt", text: location },
            ]}
          />

          <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
            <div className="card-body px-3 px-md-4 pb-3 pb-md-4 pt-0">
              <Section icon="bi-building-fill" title="Company" accent={ACCENTS.company}>
                <Fields
                  items={[
                    { label: "Agent Code", value: agent.agent_code },
                    { label: "Company Name", value: agent.company_name },
                    //{ label: "Registered As", value: humanOr(agent.registered_as, null) },
                    //{ label: "Sales Person", value: agent.sales_person },
                    { label: "T&C Agreed", value: yesNo(agent.t_and_c_agreed) },
                    { label: "Status", value: agent.status, badge: true },
                  ]}
                />
              </Section>

              <Section icon="bi-person-lines-fill" title="Contact Person" accent={ACCENTS.contact}>
                <Fields
                  items={[
                    //{ label: "Executive", value: agent.executive },
                    { label: "Name", value: fullName },
                    { label: "Gender", value: humanOr(agent.gender, null) },
                    { label: "Mobile", value: agent.mobile },
                    { label: "Email", value: agent.email },
                  ]}
                />
              </Section>

              <Section icon="bi-geo-alt-fill" title="Address" accent={ACCENTS.address}>
                <Fields
                  items={[
                     { label: "Country", value: agent.country?.name },
                     { label: "State", value: agent.state?.name },
                    { label: "City", value: agent.city?.name },
                    { label: "Pincode", value: agent.pincode },
                    { label: "Address", value: agent.address, full: true },
                  ]}
                />
              </Section>

              <Section icon="bi-wallet2" title="Wallet" accent={DETAIL_ACCENTS.green}>
                <Fields
                  items={[
                    { label: "Wallet Balance", value: money(wallet?.current_balance) },
                    { label: "Currency", value: wallet?.currency_code },
                    { label: "Wallet Status", value: wallet?.status, badge: true },
                    //{ label: "Hold Balance", value: money(wallet?.hold_balance) },
                  ]}
                />
              </Section>

              <DocumentsList documents={documents} />
              {/* <BankList banks={agent.bank_details || []} /> */}

              <Section icon="bi-clock-history" title="Record Information" accent={ACCENTS.record}>
                <Fields
                  items={[
                    { label: "Created At", value: isEmpty(agent.created_at) ? null : formatDate(agent.created_at) },
                    { label: "Last Modified", value: isEmpty(agent.modified_at) ? null : formatDate(agent.modified_at) },
                    { label: "IP Address", value: agent.ip_address },
                    { label: "Remarks", value: agent.remarks, full: true },
                  ]}
                />
              </Section>
            </div>
          </div>

          <div className="mt-3"><MarkupsSection markups={markups} /></div>
        </>
      )}
    </section>
  )
}

export default AgentDetails;
